import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { notify } from '@/lib/notifications/engine';
import { ArrowLeft, FileText, Users, Clock, DollarSign, AlertTriangle, Download, CheckCircle, Eye, Play, Trash2 } from 'lucide-react';

export default function PayrollBatchDetailPage() {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const [batch, setBatch] = useState(null);
  const [lineItems, setLineItems] = useState([]);
  const [staffSummary, setStaffSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState('summary');

  useEffect(() => {
    fetchData();
  }, [batchId]);

  const fetchData = async () => {
    try {
      const [batchRes, lineItemsRes, summaryRes] = await Promise.all([
        api.get(`/payroll/batches/${batchId}`),
        api.get(`/payroll/batches/${batchId}/line-items`),
        api.get(`/payroll/batches/${batchId}/staff-summary`)
      ]);
      setBatch(batchRes.data.data);
      setLineItems(lineItemsRes.data.items || []);
      setStaffSummary(summaryRes.data);
    } catch (e) {
      notify.error(e, 'PAYROLL_BATCH_LOAD_FAILED');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (action, label) => {
    setProcessing(true);
    try {
      await api.post(`/payroll/batches/${batchId}/${action}`);
      toast.success(`Batch ${label} successfully`);
      fetchData();
    } catch (error) {
      notify.error(error, 'PAYROLL_BATCH_ACTION_FAILED');
    } finally {
      setProcessing(false);
    }
  };

  const handleExportCSV = async () => {
    setProcessing(true);
    try {
      const response = await api.get(`/payroll/batches/${batchId}/export-csv`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payroll_${batch?.batch_no || batchId}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Payroll exported');
      fetchData();
    } catch (error) {
      notify.error(error, 'PAYROLL_BATCH_EXPORT_FAILED');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      'DRAFT': <Badge variant="outline"><FileText className="h-3 w-3 mr-1" />Draft</Badge>,
      'REVIEW': <Badge className="bg-yellow-500"><Eye className="h-3 w-3 mr-1" />Review</Badge>,
      'APPROVED': <Badge className="bg-blue-500"><CheckCircle className="h-3 w-3 mr-1" />Approved</Badge>,
      'EXPORTED': <Badge className="bg-green-500"><Download className="h-3 w-3 mr-1" />Exported</Badge>,
      'VOID': <Badge variant="destructive"><Trash2 className="h-3 w-3 mr-1" />Void</Badge>,
    };
    return badges[status] || <Badge variant="outline">{status}</Badge>;
  };

  const getHourTypeBadge = (type) => {
    const colors = {
      'ORDINARY': 'bg-gray-500',
      'WEEKEND': 'bg-blue-500',
      'HOLIDAY': 'bg-purple-500',
      'OVERTIME': 'bg-orange-500',
      'SLEEPOVER': 'bg-green-500',
    };
    return <Badge className={colors[type] || 'bg-gray-400'}>{type}</Badge>;
  };

  if (loading) {
    return <div className="flex items-center justify-center p-8"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  }

  if (!batch) {
    return <div className="text-center p-8">Batch not found</div>;
  }

  return (
    <div className="space-y-6" data-testid="payroll-batch-detail-page">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/finance/payroll')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Batches
        </Button>
      </div>

      <div className="flex justify-between items-start">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold">{batch.batch_no}</h1>
            {getStatusBadge(batch.status)}
          </div>
          <p className="text-muted-foreground">
            Period: {batch.period_no} ({batch.period_start_date} - {batch.period_end_date})
          </p>
        </div>
        <div className="flex gap-2">
          {batch.status === 'DRAFT' && (
            <>
              <Button variant="outline" onClick={() => handleAction('generate', 'generated')} disabled={processing}>
                <Play className="h-4 w-4 mr-2" />Generate
              </Button>
              <Button variant="ghost" onClick={() => handleAction('void', 'voided')} disabled={processing}>
                <Trash2 className="h-4 w-4 mr-2" />Void
              </Button>
            </>
          )}
          {batch.status === 'REVIEW' && (
            <Button onClick={() => handleAction('approve', 'approved')} disabled={processing}>
              <CheckCircle className="h-4 w-4 mr-2" />Approve
            </Button>
          )}
          {batch.status === 'APPROVED' && (
            <Button onClick={handleExportCSV} disabled={processing}>
              <Download className="h-4 w-4 mr-2" />Export CSV
            </Button>
          )}
          {(batch.exception_count || 0) > 0 && (
            <Button variant="outline" onClick={() => navigate(`/finance/payroll/${batchId}/exceptions`)}>
              <AlertTriangle className="h-4 w-4 mr-2" />
              {batch.exception_count} Exceptions
            </Button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Staff</p>
                <p className="text-2xl font-bold">{batch.staff_count || 0}</p>
              </div>
              <Users className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Hours</p>
                <p className="text-2xl font-bold">{batch.total_hours || 0}</p>
              </div>
              <Clock className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Cost</p>
                <p className="text-2xl font-bold">${(batch.total_cost || 0).toLocaleString('en-AU', { minimumFractionDigits: 2 })}</p>
              </div>
              <DollarSign className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Line Items</p>
                <p className="text-2xl font-bold">{batch.line_item_count || lineItems.length}</p>
              </div>
              <FileText className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="summary">Staff Summary</TabsTrigger>
          <TabsTrigger value="details">Line Items</TabsTrigger>
        </TabsList>

        <TabsContent value="summary">
          <Card>
            <CardHeader>
              <CardTitle>Staff Summary</CardTitle>
              <CardDescription>Aggregated pay per staff member</CardDescription>
            </CardHeader>
            <CardContent>
              {staffSummary?.data?.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="h-12 w-12 mx-auto mb-4 opacity-20" />
                  <p>No staff data available</p>
                  <p className="text-sm">Generate the batch to see staff summary</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Staff</TableHead>
                      <TableHead className="text-right">Hours</TableHead>
                      <TableHead className="text-right">Allowances</TableHead>
                      <TableHead className="text-right">Total Pay</TableHead>
                      <TableHead className="text-right">Line Items</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {staffSummary?.data?.map((staff, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{staff.staff_name || 'Unknown'}</p>
                            <p className="text-xs text-muted-foreground">{staff.staff_email}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">{staff.total_hours}</TableCell>
                        <TableCell className="text-right">${staff.total_allowances?.toFixed(2)}</TableCell>
                        <TableCell className="text-right font-medium">${staff.total_pay?.toLocaleString('en-AU', { minimumFractionDigits: 2 })}</TableCell>
                        <TableCell className="text-right">{staff.line_items}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="details">
          <Card>
            <CardHeader>
              <CardTitle>Line Items</CardTitle>
              <CardDescription>Detailed payroll calculations</CardDescription>
            </CardHeader>
            <CardContent>
              {lineItems.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-4 opacity-20" />
                  <p>No line items yet</p>
                  <p className="text-sm">Generate the batch to create line items</p>
                </div>
              ) : (
                <Table data-testid="line-items-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Staff</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">Hours</TableHead>
                      <TableHead className="text-right">Rate</TableHead>
                      <TableHead className="text-right">Multiplier</TableHead>
                      <TableHead className="text-right">Allowances</TableHead>
                      <TableHead className="text-right">Total Pay</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lineItems.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{item.staff_name || item.staff_id}</TableCell>
                        <TableCell>{item.shift_date}</TableCell>
                        <TableCell>{getHourTypeBadge(item.hour_type)}</TableCell>
                        <TableCell className="text-right">{item.hours}</TableCell>
                        <TableCell className="text-right">${item.base_rate?.toFixed(2)}</TableCell>
                        <TableCell className="text-right">x{item.multiplier}</TableCell>
                        <TableCell className="text-right">${item.allowances?.toFixed(2)}</TableCell>
                        <TableCell className="text-right font-medium">${item.total_pay?.toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
